import { create } from "zustand";
import type {
  ComponentAssetType,
  CollectionType,
  TagType,
} from "@/utils/types";
import { Database } from "@/utils/supabase.types";

type Collection = Database["reactcomponents"]["Tables"]["collections"]["Row"];

export enum CommunityPanelSections {
  Featured,
  UIKits,
  Categories,
}

export enum ViewMode {
  Grid,
  List,
}

interface CommunityPanelState {
  // Search and filter state
  searchVal: string;
  showFilters: boolean;
  searchTrigger: number;

  // Section state
  selectedSection: CommunityPanelSections;

  // View mode state
  viewMode: ViewMode;

  // Data state
  components: ComponentAssetType[];
  collections: CollectionType[];
  categories: TagType[];

  // Pagination state
  page: number;
  hasMore: boolean;
  isLoading: boolean;

  // Component counts state
  componentCounts: {
    collections: Record<string, number>;
    tags: Record<string, number>; // Combined layouts and other tags
  };

  // Selection state
  selectedCategory: string | null;
  selectedLayout: string | null;
  selectedCollections: string[];
  selectedTags: string[];

  // Filter Popover State
  isFilterPopoverOpen: boolean;
  filterSearchVal: string;
  allCollectionsForFilter: CollectionType[];
  filteredCollections: Collection[];

  // Actions
  setSearchVal: (value: string) => void;
  setShowFilters: (value: boolean) => void;
  setSelectedSection: (value: CommunityPanelSections) => void;
  setViewMode: (value: ViewMode) => void;
  toggleViewMode: () => void;
  setComponents: (components: ComponentAssetType[]) => void;
  setCollections: (collections: CollectionType[]) => void;
  setCategories: (categories: TagType[]) => void;
  setSelectedCategory: (value: string | null) => void;
  setSelectedLayout: (value: string | null) => void;
  toggleCollection: (collectionId: string) => void;
  setSelectedCollections: (collections: string[]) => void;
  toggleTag: (tagId: string) => void;
  setTags: (tagIds: string[]) => void;

  incrementSearchTrigger: () => void;
  setPage: (page: number) => void;
  setHasMore: (hasMore: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setComponentCounts: (counts: {
    collections: Record<string, number>;
    tags: Record<string, number>;
  }) => void;

  resetSelections: () => void;
  resetPagination: () => void;
  resetFilters: () => void;

  setFilterPopoverOpen: (isOpen: boolean) => void;
  setFilterSearchVal: (searchVal: string) => void;
  setAllCollectionsForFilter: (collections: CollectionType[]) => void;
}

// Updated Initial State
const initialComponentCounts = {
  collections: {},
  tags: {}, // Removed layouts field
};

export const useCommunityPanelStore = create<CommunityPanelState>()(
  (set, get) => ({
    // Initial state
    searchVal: "",
    showFilters: false,
    selectedSection: CommunityPanelSections.Featured,
    viewMode: ViewMode.Grid,
    components: [],
    collections: [],
    categories: [],
    selectedCategory: null,
    selectedLayout: null,
    selectedCollections: [],
    selectedTags: [],

    // Initial state for new properties
    searchTrigger: 0,
    page: 0,
    hasMore: true,
    isLoading: false,
    componentCounts: initialComponentCounts,

    // Filter Popover Initial State
    isFilterPopoverOpen: false,
    filterSearchVal: "",
    allCollectionsForFilter: [],
    filteredCollections: [],

    // Actions
    setSearchVal: (value) => set({ searchVal: value }),
    setShowFilters: (value) => set({ showFilters: value }),
    setSelectedSection: (value) => set({ selectedSection: value }),
    setViewMode: (value) => set({ viewMode: value }),
    toggleViewMode: () =>
      set((state) => ({
        viewMode:
          state.viewMode === ViewMode.Grid ? ViewMode.List : ViewMode.Grid,
      })),
    setComponents: (components) => set({ components }),
    setCollections: (collections) => set({ collections }),
    setCategories: (categories) => set({ categories }),
    setSelectedCategory: (value) => set({ selectedCategory: value }),
    setSelectedLayout: (value) => set({ selectedLayout: value }),
    toggleCollection: (collectionId) =>
      set((state) => ({
        selectedCollections: state.selectedCollections.includes(collectionId)
          ? state.selectedCollections.filter((id) => id !== collectionId)
          : [...state.selectedCollections, collectionId],
      })),
    setSelectedCollections: (collectionIds) =>
      set({ selectedCollections: collectionIds }),

    toggleTag: (tagId) =>
      set((state) => ({
        selectedTags: state.selectedTags.includes(tagId)
          ? state.selectedTags.filter((id) => id !== tagId)
          : [...state.selectedTags, tagId],
      })),

    setTags: (tagIds) => set({ selectedTags: tagIds }),

    incrementSearchTrigger: () =>
      set((state) => ({ searchTrigger: state.searchTrigger + 1 })),
    setPage: (page) => set({ page }),
    setHasMore: (hasMore) => set({ hasMore }),
    setIsLoading: (isLoading) => set({ isLoading }),
    setComponentCounts: (componentCounts) => set({ componentCounts }),

    setFilterPopoverOpen: (isOpen) => set({ isFilterPopoverOpen: isOpen }),

    setFilterSearchVal: (searchVal) => {
      set({ filterSearchVal: searchVal });
      const lowerSearch = searchVal.toLowerCase();
      const filtered = get().allCollectionsForFilter.filter((col) =>
        col.name.toLowerCase().includes(lowerSearch),
      );
      set({ filteredCollections: filtered });
    },
    setAllCollectionsForFilter: (collections) =>
      set({
        allCollectionsForFilter: collections,
        filteredCollections: collections,
      }),

    resetSelections: () =>
      set({
        selectedCategory: null,
        selectedLayout: null,
        selectedTags: [],
        selectedCollections: [],
      }),
    resetPagination: () =>
      set({
        page: 0,
        hasMore: true,
      }),
    resetFilters: () =>
      set({
        selectedTags: [],
        filterSearchVal: "",
        selectedCollections: [],
        filteredCollections: get().allCollectionsForFilter,
        isFilterPopoverOpen: false,
      }),
  }),
);
